import React from "react";
import { Badge, Button, ListGroup } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { AnimationType } from "../../types/animations";
import { getAnimationPreset } from "./presets";

interface AnimationTimelineProps {
  sequence: AnimationType[];
  onRemove: (index: number) => void;
  onClear: () => void;
  onPlay: () => void;
  onStop: () => void;
  isPlaying: boolean;
}

const AnimationTimeline: React.FC<AnimationTimelineProps> = ({
  sequence,
  onRemove,
  onClear,
  onPlay,
  onStop,
  isPlaying,
}) => {
  return (
    <div className="animation-timeline card shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="card-title mb-1">Animation Timeline</h5>
            <p className="text-muted mb-0 small">
              Stack animations to play them sequentially on the selected element.
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" onClick={onClear} disabled={sequence.length === 0}>
              <Icon icon="solar:trash-bin-minimalistic-linear" className="me-1" /> Clear
            </Button>
            {isPlaying ? (
              <Button variant="warning" size="sm" onClick={onStop}>
                <Icon icon="solar:pause-bold" className="me-1" /> Pause
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={onPlay} disabled={sequence.length === 0}>
                <Icon icon="solar:play-bold" className="me-1" /> Play
              </Button>
            )}
          </div>
        </div>

        {sequence.length === 0 ? (
          <div className="empty-state text-center p-4 border rounded-3">
            <Icon icon="solar:playlist-2-linear" width={32} height={32} className="mb-2 text-muted" />
            <p className="text-muted mb-0">Queue animations from the grid to build a custom choreography.</p>
          </div>
        ) : (
          <ListGroup variant="flush" className="animation-sequence">
            {sequence.map((animation, index) => {
              const preset = getAnimationPreset(animation);
              return (
                <ListGroup.Item key={`${animation}-${index}`} className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <Badge bg="primary" className="sequence-index">
                      {index + 1}
                    </Badge>
                    <div>
                      <strong>{preset?.label ?? animation}</strong>
                      <div className="text-muted small">
                        {preset ? preset.value : animation}
                      </div>
                    </div>
                  </div>
                  <Button variant="outline-danger" size="sm" onClick={() => onRemove(index)}>
                    <Icon icon="solar:close-square-line-duotone" />
                  </Button>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}
      </div>
    </div>
  );
};

export default AnimationTimeline;
